/**
 * "In {town}" (locative) and genitive forms for every current Finnish municipality (kunta and
 * kaupunki, 307 of 308; Koski Tl. is left out because the gazetteer has no such place name).
 *
 * Source: Kotimaisten kielten keskus (Kotus), Asutusnimihakemisto,
 * https://kaino.kotus.fi/asutusnimihakemisto/ (licence CC BY 4.0), fetched 2026-09-29 with
 * `?a=listaus&laji=kunnat`. The locative is Kotus's first-listed form: the one the town itself uses,
 * inessive (-ssa/-ssä) or adessive (-lla/-llä), e.g. Iisalmessa, Vantaalla, Paraisilla. Where
 * Kotus lists two (Nokialla, Nokiassa) the first is kept.
 *
 * The genitive is Kotus's explicit "gen." form where given (Virtain, Kangasalan, Juuan), else the
 * locative's stem + n (Iisalmen, Vantaan). Two groups needed more than that:
 * - -inen names that inflect as plurals (Huittisissa -> Huittisten, Kauniaisissa -> Kauniaisten);
 * - names where both parts inflect (Uudessakaupungissa -> Uudenkaupungin, Isollajoella ->
 *   Isonjoen, Isossakyrössä -> Isonkyrön, Uudessakaarlepyyssä -> Uudenkaarlepyyn).
 *
 * Keys are the Finnish names. The Swedish-majority municipalities the gazetteer labels in Swedish
 * even on fi pages (Lovisa, Raseborg, Pargas, ...) are deliberately not keyed by their Swedish
 * name: their fi pages say "Lovisa" everywhere, so they keep the case-free fallback rather than
 * a "Loviisassa" that would not match the rest of the page.
 */
export interface FinnishMunicipalityForms {
  /** "In {town}": Iisalmessa, Vantaalla. */
  locative: string;
  /** Genitive: Iisalmen, Vantaan, Huittisten. */
  genitive: string;
}

export const FINNISH_MUNICIPALITY_FORMS: Record<string, FinnishMunicipalityForms> = {
  "Akaa": { locative: "Akaassa", genitive: "Akaan" },
  "Alajärvi": { locative: "Alajärvellä", genitive: "Alajärven" },
  "Alavieska": { locative: "Alavieskassa", genitive: "Alavieskan" },
  "Alavus": { locative: "Alavudella", genitive: "Alavuden" },
  "Asikkala": { locative: "Asikkalassa", genitive: "Asikkalan" },
  "Askola": { locative: "Askolassa", genitive: "Askolan" },
  "Aura": { locative: "Aurassa", genitive: "Auran" },
  "Brändö": { locative: "Brändössä", genitive: "Brändön" },
  "Eckerö": { locative: "Eckerössä", genitive: "Eckerön" },
  "Enonkoski": { locative: "Enonkoskella", genitive: "Enonkosken" },
  "Enontekiö": { locative: "Enontekiöllä", genitive: "Enontekiön" },
  "Espoo": { locative: "Espoossa", genitive: "Espoon" },
  "Eura": { locative: "Eurassa", genitive: "Euran" },
  "Eurajoki": { locative: "Eurajoella", genitive: "Eurajoen" },
  "Evijärvi": { locative: "Evijärvellä", genitive: "Evijärven" },
  "Finström": { locative: "Finströmissä", genitive: "Finströmin" },
  "Forssa": { locative: "Forssassa", genitive: "Forssan" },
  "Föglö": { locative: "Föglössä", genitive: "Föglön" },
  "Geta": { locative: "Getassa", genitive: "Getan" },
  "Haapajärvi": { locative: "Haapajärvellä", genitive: "Haapajärven" },
  "Haapavesi": { locative: "Haapavedellä", genitive: "Haapaveden" },
  "Hailuoto": { locative: "Hailuodossa", genitive: "Hailuodon" },
  "Halsua": { locative: "Halsualla", genitive: "Halsuan" },
  "Hamina": { locative: "Haminassa", genitive: "Haminan" },
  "Hammarland": { locative: "Hammarlandissa", genitive: "Hammarlandin" },
  "Hankasalmi": { locative: "Hankasalmella", genitive: "Hankasalmen" },
  "Hanko": { locative: "Hangossa", genitive: "Hangon" },
  "Harjavalta": { locative: "Harjavallassa", genitive: "Harjavallan" },
  "Hartola": { locative: "Hartolassa", genitive: "Hartolan" },
  "Hattula": { locative: "Hattulassa", genitive: "Hattulan" },
  "Hausjärvi": { locative: "Hausjärvellä", genitive: "Hausjärven" },
  "Heinola": { locative: "Heinolassa", genitive: "Heinolan" },
  "Heinävesi": { locative: "Heinävedellä", genitive: "Heinäveden" },
  "Helsinki": { locative: "Helsingissä", genitive: "Helsingin" },
  "Hirvensalmi": { locative: "Hirvensalmella", genitive: "Hirvensalmen" },
  "Hollola": { locative: "Hollolassa", genitive: "Hollolan" },
  "Huittinen": { locative: "Huittisissa", genitive: "Huittisten" },
  "Humppila": { locative: "Humppilassa", genitive: "Humppilan" },
  "Hyrynsalmi": { locative: "Hyrynsalmella", genitive: "Hyrynsalmen" },
  "Hyvinkää": { locative: "Hyvinkäällä", genitive: "Hyvinkään" },
  "Hämeenkyrö": { locative: "Hämeenkyrössä", genitive: "Hämeenkyrön" },
  "Hämeenlinna": { locative: "Hämeenlinnassa", genitive: "Hämeenlinnan" },
  "Ii": { locative: "Iissä", genitive: "Iin" },
  "Iisalmi": { locative: "Iisalmessa", genitive: "Iisalmen" },
  "Iitti": { locative: "Iitissä", genitive: "Iitin" },
  "Ikaalinen": { locative: "Ikaalisissa", genitive: "Ikaalisten" },
  "Ilmajoki": { locative: "Ilmajoella", genitive: "Ilmajoen" },
  "Ilomantsi": { locative: "Ilomantsissa", genitive: "Ilomantsin" },
  "Imatra": { locative: "Imatralla", genitive: "Imatran" },
  "Inari": { locative: "Inarissa", genitive: "Inarin" },
  "Inkoo": { locative: "Inkoossa", genitive: "Inkoon" },
  "Isojoki": { locative: "Isollajoella", genitive: "Isonjoen" },
  "Isokyrö": { locative: "Isossakyrössä", genitive: "Isonkyrön" },
  "Janakkala": { locative: "Janakkalassa", genitive: "Janakkalan" },
  "Joensuu": { locative: "Joensuussa", genitive: "Joensuun" },
  "Jokioinen": { locative: "Jokioisissa", genitive: "Jokioisten" },
  "Jomala": { locative: "Jomalassa", genitive: "Jomalan" },
  "Joroinen": { locative: "Joroisissa", genitive: "Joroisten" },
  "Joutsa": { locative: "Joutsassa", genitive: "Joutsan" },
  "Juuka": { locative: "Juuassa", genitive: "Juuan" },
  "Juupajoki": { locative: "Juupajoella", genitive: "Juupajoen" },
  "Juva": { locative: "Juvalla", genitive: "Juvan" },
  "Jyväskylä": { locative: "Jyväskylässä", genitive: "Jyväskylän" },
  "Jämijärvi": { locative: "Jämijärvellä", genitive: "Jämijärven" },
  "Jämsä": { locative: "Jämsässä", genitive: "Jämsän" },
  "Järvenpää": { locative: "Järvenpäässä", genitive: "Järvenpään" },
  "Kaarina": { locative: "Kaarinassa", genitive: "Kaarinan" },
  "Kaavi": { locative: "Kaavilla", genitive: "Kaavin" },
  "Kajaani": { locative: "Kajaanissa", genitive: "Kajaanin" },
  "Kalajoki": { locative: "Kalajoella", genitive: "Kalajoen" },
  "Kangasala": { locative: "Kangasalla", genitive: "Kangasalan" },
  "Kangasniemi": { locative: "Kangasniemellä", genitive: "Kangasniemen" },
  "Kankaanpää": { locative: "Kankaanpäässä", genitive: "Kankaanpään" },
  "Kannonkoski": { locative: "Kannonkoskella", genitive: "Kannonkosken" },
  "Kannus": { locative: "Kannuksessa", genitive: "Kannuksen" },
  "Karijoki": { locative: "Karijoella", genitive: "Karijoen" },
  "Karkkila": { locative: "Karkkilassa", genitive: "Karkkilan" },
  "Karstula": { locative: "Karstulassa", genitive: "Karstulan" },
  "Karvia": { locative: "Karviassa", genitive: "Karvian" },
  "Kaskinen": { locative: "Kaskisissa", genitive: "Kaskisten" },
  "Kauhajoki": { locative: "Kauhajoella", genitive: "Kauhajoen" },
  "Kauhava": { locative: "Kauhavalla", genitive: "Kauhavan" },
  "Kauniainen": { locative: "Kauniaisissa", genitive: "Kauniaisten" },
  "Kaustinen": { locative: "Kaustisella", genitive: "Kaustisen" },
  "Keitele": { locative: "Keiteleellä", genitive: "Keiteleen" },
  "Kemi": { locative: "Kemissä", genitive: "Kemin" },
  "Kemijärvi": { locative: "Kemijärvellä", genitive: "Kemijärven" },
  "Keminmaa": { locative: "Keminmaassa", genitive: "Keminmaan" },
  "Kemiönsaari": { locative: "Kemiönsaaressa", genitive: "Kemiönsaaren" },
  "Kempele": { locative: "Kempeleessä", genitive: "Kempeleen" },
  "Kerava": { locative: "Keravalla", genitive: "Keravan" },
  "Keuruu": { locative: "Keuruulla", genitive: "Keuruun" },
  "Kihniö": { locative: "Kihniössä", genitive: "Kihniön" },
  "Kinnula": { locative: "Kinnulassa", genitive: "Kinnulan" },
  "Kirkkonummi": { locative: "Kirkkonummella", genitive: "Kirkkonummen" },
  "Kitee": { locative: "Kiteellä", genitive: "Kiteen" },
  "Kittilä": { locative: "Kittilässä", genitive: "Kittilän" },
  "Kiuruvesi": { locative: "Kiuruvedellä", genitive: "Kiuruveden" },
  "Kivijärvi": { locative: "Kivijärvellä", genitive: "Kivijärven" },
  "Kokemäki": { locative: "Kokemäellä", genitive: "Kokemäen" },
  "Kokkola": { locative: "Kokkolassa", genitive: "Kokkolan" },
  "Kolari": { locative: "Kolarissa", genitive: "Kolarin" },
  "Konnevesi": { locative: "Konnevedellä", genitive: "Konneveden" },
  "Kontiolahti": { locative: "Kontiolahdessa", genitive: "Kontiolahden" },
  "Korsnäs": { locative: "Korsnäsissä", genitive: "Korsnäsin" },
  "Kotka": { locative: "Kotkassa", genitive: "Kotkan" },
  "Kouvola": { locative: "Kouvolassa", genitive: "Kouvolan" },
  "Kristiinankaupunki": { locative: "Kristiinankaupungissa", genitive: "Kristiinankaupungin" },
  "Kruunupyy": { locative: "Kruunupyyssä", genitive: "Kruunupyyn" },
  "Kuhmo": { locative: "Kuhmossa", genitive: "Kuhmon" },
  "Kuhmoinen": { locative: "Kuhmoisissa", genitive: "Kuhmoisten" },
  "Kumlinge": { locative: "Kumlingessa", genitive: "Kumlingen" },
  "Kuopio": { locative: "Kuopiossa", genitive: "Kuopion" },
  "Kuortane": { locative: "Kuortaneella", genitive: "Kuortaneen" },
  "Kurikka": { locative: "Kurikassa", genitive: "Kurikan" },
  "Kustavi": { locative: "Kustavissa", genitive: "Kustavin" },
  "Kuusamo": { locative: "Kuusamossa", genitive: "Kuusamon" },
  "Kyyjärvi": { locative: "Kyyjärvellä", genitive: "Kyyjärven" },
  "Kärkölä": { locative: "Kärkölässä", genitive: "Kärkölän" },
  "Kärsämäki": { locative: "Kärsämäellä", genitive: "Kärsämäen" },
  "Kökar": { locative: "Kökarissa", genitive: "Kökarin" },
  "Lahti": { locative: "Lahdessa", genitive: "Lahden" },
  "Laihia": { locative: "Laihialla", genitive: "Laihian" },
  "Laitila": { locative: "Laitilassa", genitive: "Laitilan" },
  "Lapinjärvi": { locative: "Lapinjärvellä", genitive: "Lapinjärven" },
  "Lapinlahti": { locative: "Lapinlahdella", genitive: "Lapinlahden" },
  "Lappajärvi": { locative: "Lappajärvellä", genitive: "Lappajärven" },
  "Lappeenranta": { locative: "Lappeenrannassa", genitive: "Lappeenrannan" },
  "Lapua": { locative: "Lapualla", genitive: "Lapuan" },
  "Laukaa": { locative: "Laukaassa", genitive: "Laukaan" },
  "Lemi": { locative: "Lemillä", genitive: "Lemin" },
  "Lemland": { locative: "Lemlandissa", genitive: "Lemlandin" },
  "Lempäälä": { locative: "Lempäälässä", genitive: "Lempäälän" },
  "Leppävirta": { locative: "Leppävirralla", genitive: "Leppävirran" },
  "Lestijärvi": { locative: "Lestijärvellä", genitive: "Lestijärven" },
  "Lieksa": { locative: "Lieksassa", genitive: "Lieksan" },
  "Lieto": { locative: "Liedossa", genitive: "Liedon" },
  "Liminka": { locative: "Limingassa", genitive: "Limingan" },
  "Liperi": { locative: "Liperissä", genitive: "Liperin" },
  "Lohja": { locative: "Lohjalla", genitive: "Lohjan" },
  "Loimaa": { locative: "Loimaalla", genitive: "Loimaan" },
  "Loppi": { locative: "Lopella", genitive: "Lopen" },
  "Loviisa": { locative: "Loviisassa", genitive: "Loviisan" },
  "Luhanka": { locative: "Luhangassa", genitive: "Luhangan" },
  "Lumijoki": { locative: "Lumijoella", genitive: "Lumijoen" },
  "Lumparland": { locative: "Lumparlandissa", genitive: "Lumparlandin" },
  "Luoto": { locative: "Luodossa", genitive: "Luodon" },
  "Luumäki": { locative: "Luumäellä", genitive: "Luumäen" },
  "Maalahti": { locative: "Maalahdessa", genitive: "Maalahden" },
  "Maarianhamina": { locative: "Maarianhaminassa", genitive: "Maarianhaminan" },
  "Marttila": { locative: "Marttilassa", genitive: "Marttilan" },
  "Masku": { locative: "Maskussa", genitive: "Maskun" },
  "Merijärvi": { locative: "Merijärvellä", genitive: "Merijärven" },
  "Merikarvia": { locative: "Merikarvialla", genitive: "Merikarvian" },
  "Miehikkälä": { locative: "Miehikkälässä", genitive: "Miehikkälän" },
  "Mikkeli": { locative: "Mikkelissä", genitive: "Mikkelin" },
  "Muhos": { locative: "Muhoksella", genitive: "Muhoksen" },
  "Multia": { locative: "Multialla", genitive: "Multian" },
  "Muonio": { locative: "Muoniossa", genitive: "Muonion" },
  "Mustasaari": { locative: "Mustasaaressa", genitive: "Mustasaaren" },
  "Muurame": { locative: "Muuramessa", genitive: "Muuramen" },
  "Mynämäki": { locative: "Mynämäessä", genitive: "Mynämäen" },
  "Myrskylä": { locative: "Myrskylässä", genitive: "Myrskylän" },
  "Mäntsälä": { locative: "Mäntsälässä", genitive: "Mäntsälän" },
  "Mänttä-Vilppula": { locative: "Mänttä-Vilppulassa", genitive: "Mänttä-Vilppulan" },
  "Mäntyharju": { locative: "Mäntyharjussa", genitive: "Mäntyharjun" },
  "Naantali": { locative: "Naantalissa", genitive: "Naantalin" },
  "Nakkila": { locative: "Nakkilassa", genitive: "Nakkilan" },
  "Nivala": { locative: "Nivalassa", genitive: "Nivalan" },
  "Nokia": { locative: "Nokialla", genitive: "Nokian" },
  "Nousiainen": { locative: "Nousiaisissa", genitive: "Nousiaisten" },
  "Nurmes": { locative: "Nurmeksessa", genitive: "Nurmeksen" },
  "Nurmijärvi": { locative: "Nurmijärvellä", genitive: "Nurmijärven" },
  "Närpiö": { locative: "Närpiössä", genitive: "Närpiön" },
  "Orimattila": { locative: "Orimattilassa", genitive: "Orimattilan" },
  "Oripää": { locative: "Oripäässä", genitive: "Oripään" },
  "Orivesi": { locative: "Orivedellä", genitive: "Oriveden" },
  "Oulainen": { locative: "Oulaisissa", genitive: "Oulaisten" },
  "Oulu": { locative: "Oulussa", genitive: "Oulun" },
  "Outokumpu": { locative: "Outokummussa", genitive: "Outokummun" },
  "Padasjoki": { locative: "Padasjoella", genitive: "Padasjoen" },
  "Paimio": { locative: "Paimiossa", genitive: "Paimion" },
  "Paltamo": { locative: "Paltamossa", genitive: "Paltamon" },
  "Parainen": { locative: "Paraisilla", genitive: "Paraisten" },
  "Parikkala": { locative: "Parikkalassa", genitive: "Parikkalan" },
  "Parkano": { locative: "Parkanossa", genitive: "Parkanon" },
  "Pedersören kunta": { locative: "Pedersören kunnassa", genitive: "Pedersören kunnan" },
  "Pelkosenniemi": { locative: "Pelkosenniemellä", genitive: "Pelkosenniemen" },
  "Pello": { locative: "Pellossa", genitive: "Pellon" },
  "Perho": { locative: "Perhossa", genitive: "Perhon" },
  "Petäjävesi": { locative: "Petäjävedellä", genitive: "Petäjäveden" },
  "Pieksämäki": { locative: "Pieksämäellä", genitive: "Pieksämäen" },
  "Pielavesi": { locative: "Pielavedellä", genitive: "Pielaveden" },
  "Pietarsaari": { locative: "Pietarsaaressa", genitive: "Pietarsaaren" },
  "Pihtipudas": { locative: "Pihtiputaalla", genitive: "Pihtiputaan" },
  "Pirkkala": { locative: "Pirkkalassa", genitive: "Pirkkalan" },
  "Polvijärvi": { locative: "Polvijärvellä", genitive: "Polvijärven" },
  "Pomarkku": { locative: "Pomarkussa", genitive: "Pomarkun" },
  "Pori": { locative: "Porissa", genitive: "Porin" },
  "Pornainen": { locative: "Pornaisissa", genitive: "Pornaisten" },
  "Porvoo": { locative: "Porvoossa", genitive: "Porvoon" },
  "Posio": { locative: "Posiolla", genitive: "Posion" },
  "Pudasjärvi": { locative: "Pudasjärvellä", genitive: "Pudasjärven" },
  "Pukkila": { locative: "Pukkilassa", genitive: "Pukkilan" },
  "Punkalaidun": { locative: "Punkalaitumella", genitive: "Punkalaitumen" },
  "Puolanka": { locative: "Puolangalla", genitive: "Puolangan" },
  "Puumala": { locative: "Puumalassa", genitive: "Puumalan" },
  "Pyhtää": { locative: "Pyhtäällä", genitive: "Pyhtään" },
  "Pyhäjoki": { locative: "Pyhäjoella", genitive: "Pyhäjoen" },
  "Pyhäjärvi": { locative: "Pyhäjärvellä", genitive: "Pyhäjärven" },
  "Pyhäntä": { locative: "Pyhännällä", genitive: "Pyhännän" },
  "Pyhäranta": { locative: "Pyhärannassa", genitive: "Pyhärannan" },
  "Pälkäne": { locative: "Pälkäneellä", genitive: "Pälkäneen" },
  "Pöytyä": { locative: "Pöytyällä", genitive: "Pöytyän" },
  "Raahe": { locative: "Raahessa", genitive: "Raahen" },
  "Raasepori": { locative: "Raaseporissa", genitive: "Raaseporin" },
  "Raisio": { locative: "Raisiossa", genitive: "Raision" },
  "Rantasalmi": { locative: "Rantasalmella", genitive: "Rantasalmen" },
  "Ranua": { locative: "Ranualla", genitive: "Ranuan" },
  "Rauma": { locative: "Raumalla", genitive: "Rauman" },
  "Rautalampi": { locative: "Rautalammilla", genitive: "Rautalammin" },
  "Rautavaara": { locative: "Rautavaaralla", genitive: "Rautavaaran" },
  "Rautjärvi": { locative: "Rautjärvellä", genitive: "Rautjärven" },
  "Reisjärvi": { locative: "Reisjärvellä", genitive: "Reisjärven" },
  "Riihimäki": { locative: "Riihimäellä", genitive: "Riihimäen" },
  "Ristijärvi": { locative: "Ristijärvellä", genitive: "Ristijärven" },
  "Rovaniemi": { locative: "Rovaniemellä", genitive: "Rovaniemen" },
  "Ruokolahti": { locative: "Ruokolahdella", genitive: "Ruokolahden" },
  "Ruovesi": { locative: "Ruovedellä", genitive: "Ruoveden" },
  "Rusko": { locative: "Ruskolla", genitive: "Ruskon" },
  "Rääkkylä": { locative: "Rääkkylässä", genitive: "Rääkkylän" },
  "Saarijärvi": { locative: "Saarijärvellä", genitive: "Saarijärven" },
  "Salla": { locative: "Sallassa", genitive: "Sallan" },
  "Salo": { locative: "Salossa", genitive: "Salon" },
  "Saltvik": { locative: "Saltvikissa", genitive: "Saltvikin" },
  "Sastamala": { locative: "Sastamalassa", genitive: "Sastamalan" },
  "Sauvo": { locative: "Sauvossa", genitive: "Sauvon" },
  "Savitaipale": { locative: "Savitaipaleella", genitive: "Savitaipaleen" },
  "Savonlinna": { locative: "Savonlinnassa", genitive: "Savonlinnan" },
  "Savukoski": { locative: "Savukoskella", genitive: "Savukosken" },
  "Seinäjoki": { locative: "Seinäjoella", genitive: "Seinäjoen" },
  "Sievi": { locative: "Sievissä", genitive: "Sievin" },
  "Siikainen": { locative: "Siikaisissa", genitive: "Siikaisten" },
  "Siikajoki": { locative: "Siikajoella", genitive: "Siikajoen" },
  "Siikalatva": { locative: "Siikalatvalla", genitive: "Siikalatvan" },
  "Siilinjärvi": { locative: "Siilinjärvellä", genitive: "Siilinjärven" },
  "Simo": { locative: "Simossa", genitive: "Simon" },
  "Sipoo": { locative: "Sipoossa", genitive: "Sipoon" },
  "Siuntio": { locative: "Siuntiossa", genitive: "Siuntion" },
  "Sodankylä": { locative: "Sodankylässä", genitive: "Sodankylän" },
  "Soini": { locative: "Soinissa", genitive: "Soinin" },
  "Somero": { locative: "Somerolla", genitive: "Someron" },
  "Sonkajärvi": { locative: "Sonkajärvellä", genitive: "Sonkajärven" },
  "Sotkamo": { locative: "Sotkamossa", genitive: "Sotkamon" },
  "Sottunga": { locative: "Sottungassa", genitive: "Sottungan" },
  "Sulkava": { locative: "Sulkavalla", genitive: "Sulkavan" },
  "Sund": { locative: "Sundissa", genitive: "Sundin" },
  "Suomussalmi": { locative: "Suomussalmella", genitive: "Suomussalmen" },
  "Suonenjoki": { locative: "Suonenjoella", genitive: "Suonenjoen" },
  "Sysmä": { locative: "Sysmässä", genitive: "Sysmän" },
  "Säkylä": { locative: "Säkylässä", genitive: "Säkylän" },
  "Taipalsaari": { locative: "Taipalsaarella", genitive: "Taipalsaaren" },
  "Taivalkoski": { locative: "Taivalkoskella", genitive: "Taivalkosken" },
  "Taivassalo": { locative: "Taivassalossa", genitive: "Taivassalon" },
  "Tammela": { locative: "Tammelassa", genitive: "Tammelan" },
  "Tampere": { locative: "Tampereella", genitive: "Tampereen" },
  "Tervo": { locative: "Tervossa", genitive: "Tervon" },
  "Tervola": { locative: "Tervolassa", genitive: "Tervolan" },
  "Teuva": { locative: "Teuvalla", genitive: "Teuvan" },
  "Tohmajärvi": { locative: "Tohmajärvellä", genitive: "Tohmajärven" },
  "Toholampi": { locative: "Toholammilla", genitive: "Toholammin" },
  "Toivakka": { locative: "Toivakassa", genitive: "Toivakan" },
  "Tornio": { locative: "Torniossa", genitive: "Tornion" },
  "Turku": { locative: "Turussa", genitive: "Turun" },
  "Tuusniemi": { locative: "Tuusniemellä", genitive: "Tuusniemen" },
  "Tuusula": { locative: "Tuusulassa", genitive: "Tuusulan" },
  "Tyrnävä": { locative: "Tyrnävällä", genitive: "Tyrnävän" },
  "Ulvila": { locative: "Ulvilassa", genitive: "Ulvilan" },
  "Urjala": { locative: "Urjalassa", genitive: "Urjalan" },
  "Utajärvi": { locative: "Utajärvellä", genitive: "Utajärven" },
  "Utsjoki": { locative: "Utsjoella", genitive: "Utsjoen" },
  "Uurainen": { locative: "Uuraisilla", genitive: "Uuraisten" },
  "Uusikaarlepyy": { locative: "Uudessakaarlepyyssä", genitive: "Uudenkaarlepyyn" },
  "Uusikaupunki": { locative: "Uudessakaupungissa", genitive: "Uudenkaupungin" },
  "Vaala": { locative: "Vaalassa", genitive: "Vaalan" },
  "Vaasa": { locative: "Vaasassa", genitive: "Vaasan" },
  "Valkeakoski": { locative: "Valkeakoskella", genitive: "Valkeakosken" },
  "Vantaa": { locative: "Vantaalla", genitive: "Vantaan" },
  "Varkaus": { locative: "Varkaudessa", genitive: "Varkauden" },
  "Vehmaa": { locative: "Vehmaalla", genitive: "Vehmaan" },
  "Vesanto": { locative: "Vesannolla", genitive: "Vesannon" },
  "Vesilahti": { locative: "Vesilahdessa", genitive: "Vesilahden" },
  "Veteli": { locative: "Vetelissä", genitive: "Vetelin" },
  "Vieremä": { locative: "Vieremällä", genitive: "Vieremän" },
  "Vihti": { locative: "Vihdissä", genitive: "Vihdin" },
  "Viitasaari": { locative: "Viitasaarella", genitive: "Viitasaaren" },
  "Vimpeli": { locative: "Vimpelissä", genitive: "Vimpelin" },
  "Virolahti": { locative: "Virolahdella", genitive: "Virolahden" },
  "Virrat": { locative: "Virroilla", genitive: "Virtain" },
  "Vårdö": { locative: "Vårdössä", genitive: "Vårdön" },
  "Vöyri": { locative: "Vöyrillä", genitive: "Vöyrin" },
  "Ylitornio": { locative: "Ylitorniolla", genitive: "Ylitornion" },
  "Ylivieska": { locative: "Ylivieskassa", genitive: "Ylivieskan" },
  "Ylöjärvi": { locative: "Ylöjärvellä", genitive: "Ylöjärven" },
  "Ypäjä": { locative: "Ypäjällä", genitive: "Ypäjän" },
  "Ähtäri": { locative: "Ähtärissä", genitive: "Ähtärin" },
  "Äänekoski": { locative: "Äänekoskella", genitive: "Äänekosken" },
};

const BY_LOWERCASE_NAME = new Map(
  Object.entries(FINNISH_MUNICIPALITY_FORMS).map(([name, forms]) => [name.toLowerCase(), forms] as const),
);

/** Forms for a municipality by its Finnish name (case-insensitive), or null. */
export function getFinnishMunicipalityForms(name: string): FinnishMunicipalityForms | null {
  return BY_LOWERCASE_NAME.get(name.trim().toLowerCase()) ?? null;
}
